import { useApi } from './useApi';
import { showDbNotification } from './dbNotifications';
import { trackAnalyticsEvent } from './analytics';

// Types for the new progressive data capture system
export interface TempHomeowner {
  id: string;
  temp_session_id: string;
  address?: string;
  name?: string;
  phone?: string;
  sms_opt_in: boolean;
  meta?: { questionnaireData?: Record<string, unknown>; questionnaire?: Record<string, unknown> };
  created_at: string;
  updated_at: string;
}

export interface Homeowner {
  id: string;
  userId?: string;
  address?: string;
  firstName?: string;
  lastName?: string;
  created_at: string;
  updated_at: string;
  projects?: Project[];
  user?: User;
}

export interface Project {
  id: string;
  homeowner_id?: string;
  temp_homeowner_id?: string;
  project_type: 'ROOF' | 'SIDING' | 'GUTTERS' | 'WINDOWS' | 'DOORS' | 'OTHER';
  q1?: string;
  q2?: string;
  q3?: string;
  q4?: string;
  q5?: string;
  q6?: string;
  q7?: string;
  q8?: string;
  q9?: string;
  created_at: string;
  updated_at: string;
  homeowner?: Homeowner;
  temp_homeowner?: TempHomeowner;
}

export interface User {
  id: string;
  clerkId: string;
  email: string;
  phone?: string;
  role: 'HOMEOWNER' | 'CONTRACTOR';
  created_at: string;
  updated_at: string;
}

export interface Contractor {
  id: string;
  user_id: string;
  verified: boolean;
  created_at: string;
  updated_at: string;
  verification?: ContractorVerification;
}

export interface ContractorVerification {
  id: string;
  contractor_id: string;
  license_number?: string;
  insurance_proof_url?: string;
  w9_url?: string;
  business_name?: string;
  business_address?: string;
  additional_docs?: any;
  created_at: string;
  updated_at: string;
}

// CB:BEGIN canonical-address-resolver-progressive
/**
 * Canonical helper to resolve the best available address from localStorage.
 * enteredAddress is canonical; legacy keys are fallback only.
 * NEVER returns "TBD" or other placeholder values.
 * Returns undefined if no valid address is found (so it can be omitted from payload).
 */
function resolveAddress(): string | undefined {
  const bad = new Set(['TBD', 'Address TBD', 'Address to be provided']);

  // Helper to safely read/trim values and parse pendingUserAddress JSON if needed
  const safeRead = (key: string): string => {
    try {
      const value = localStorage.getItem(key);
      if (!value) return '';
      const trimmed = value.trim();
      if (!trimmed) return '';
      
      // If it's pendingUserAddress, try to parse as JSON
      if (key === 'pendingUserAddress') {
        try {
          const parsed = JSON.parse(trimmed);
          return parsed?.fullAddress || trimmed;
        } catch {
          return trimmed;
        }
      }
      return trimmed;
    } catch {
      return '';
    }
  };

  // Priority order: enteredAddress (canonical) > pendingUserAddress (legacy) > last_address (legacy)
  const candidates = [
    safeRead('enteredAddress'),      // 1. Most recent, canonical
    safeRead('pendingUserAddress'),   // 2. Legacy fallback
    safeRead('last_address'),        // 3. Legacy fallback
  ];

  for (const c of candidates) {
    if (c && !bad.has(c)) return c;
  }
  return undefined;
}
// CB:END canonical-address-resolver-progressive

// Progressive Data Capture Service
export function useProgressiveDataService() {
  const { request } = useApi();

  // Debounce map for temp homeowner calls
  const debounceMap = new Map<string, NodeJS.Timeout>();

  type ProjectTypeOption = Project['project_type'];

  interface EnsureActiveProjectOptions {
    homeownerId?: string;
    tempHomeownerId?: string;
    tempSessionId?: string;
    projectType?: ProjectTypeOption | string | null;
    address?: string | null;
    logLabel?: string;
  }

  // Temp Homeowner APIs
  const createOrUpdateTempHomeowner = async (data: {
    temp_session_id: string;
    address?: string;
    zip_code?: string;
    name?: string;
    phone?: string;
    sms_opt_in?: boolean;
    quick_estimate?: string;
    q1?: string;
    q2?: string;
    q3?: string;
    q4?: string;
    q5?: string;
    q6?: string;
    q7?: string;
  }): Promise<TempHomeowner> => {
    // CB:BEGIN never-send-empty-address
    // Resolve address from localStorage if not provided, but NEVER send empty/TBD
    const resolvedAddress = data.address || resolveAddress();
    const payload = {
      ...data,
      ...(resolvedAddress ? { address: resolvedAddress } : {}), // Only include if valid
    };
    // CB:END never-send-empty-address
    
    // Clear existing debounce for this session
    const existingTimeout = debounceMap.get(data.temp_session_id);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Debounce the API call by 300ms
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(async () => {
        try {
          const res = await request<TempHomeowner>('/api/temp-homeowner', {
            method: 'POST',
            body: payload // Use payload instead of data
          });
          showDbNotification({ table: 'temp_homeowners', action: 'upsert', payload: { ...data, id: res.id } });
          debounceMap.delete(data.temp_session_id);
          resolve(res);
        } catch (error) {
          debounceMap.delete(data.temp_session_id);
          reject(error);
        }
      }, 300);
      
      debounceMap.set(data.temp_session_id, timeout);
    });
  };

  const getTempHomeowner = async (temp_session_id: string): Promise<TempHomeowner> => {
    return request<TempHomeowner>(`/api/temp-homeowner/${temp_session_id}`);
  };

  // Homeowner APIs
  const updateHomeowner = async (data: {
    user_id: string;
    address?: string;
    zip_code?: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    sms_opt_in?: boolean;
  }): Promise<Homeowner> => {
    // Parse name into first and last name if provided
    let firstName = data.firstName;
    let lastName = data.lastName;
    
    if (data.name && !firstName && !lastName) {
      const nameParts = data.name.trim().split(' ');
      firstName = nameParts[0] || '';
      lastName = nameParts.slice(1).join(' ') || '';
    }
    
    const requestData = {
      user_id: data.user_id,
      address: data.address,
      firstName,
      lastName
    };
    
    const res = await request<Homeowner>('/api/homeowner', {
      method: 'PUT',
      body: requestData
    });
    showDbNotification({ table: 'homeowners', action: 'upsert', payload: { ...requestData, id: res.id } });
    return res;
  };

  const getHomeowner = async (user_id: string): Promise<Homeowner> => {
    return request<Homeowner>(`/api/homeowner/${user_id}`);
  };

  // Project APIs
  const createProject = async (data: {
    homeowner_id?: string;
    temp_homeowner_id?: string;
    project_type?: ProjectTypeOption;
    questionnaire?: Record<string, unknown>;
    q1?: string;
    q2?: string;
    q3?: string;
    q4?: string;
    q5?: string;
    q6?: string;
    q7?: string;
    q8?: string;
    q9?: string;
  }): Promise<Project> => {
    const res = await request<Project>('/api/projects', {
      method: 'POST',
      body: data
    });
    showDbNotification({ table: 'projects', action: 'create', payload: { ...data, id: res.id } });
    trackAnalyticsEvent('project_created', {
      projectId: res.id,
      path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    });
    return res;
  };

  const updateProject = async (project_id: string, data: {
    project_type?: 'ROOF' | 'SIDING' | 'GUTTERS' | 'WINDOWS' | 'DOORS' | 'OTHER';
    q1?: string;
    q2?: string;
    q3?: string;
    q4?: string;
    q5?: string;
    q6?: string;
    q7?: string;
    q8?: string;
    q9?: string;
  }): Promise<Project> => {
    const res = await request<Project>(`/api/projects/${project_id}`, {
      method: 'PUT',
      body: data
    });
    showDbNotification({ table: 'projects', action: 'update', payload: { id: project_id, ...data } });
    return res;
  };

  const getProjects = async (params: {
    homeowner_id?: string;
    temp_homeowner_id?: string;
  }): Promise<Project[]> => {
    return request<Project[]>('/api/projects', {
      query: params
    });
  };

  // FIX: Contractor lists – current-bids and active
  async function fetchCurrentBidsForContractor(): Promise<any[]> {
    try {
      return await request<any[]>('/api/contractor/projects/current-bids', { method: 'GET' });
    } catch {
      return [];
    }
  }

  async function fetchActiveProjectsForContractor(): Promise<any[]> {
    try {
      return await request<any[]>('/api/contractor/projects/active', { method: 'GET' });
    } catch {
      return [];
    }
  }

  // Signup API
  const processSignup = async (data: {
    user_id: string;
    email: string;
    account_type: 'HOMEOWNER' | 'CONTRACTOR' | 'REALTOR'; // CB:BEGIN fr-014-realtor-type
    temp_session_id?: string;
  }): Promise<{ success: boolean; user: User; message: string }> => {
    // CB:END fr-014-realtor-type
    const res = await request<any>('/api/signup', {
      method: 'POST',
      body: {
        user_id: data.user_id,
        email: data.email,
        account_type: data.account_type,
        temp_session_id: data.temp_session_id
      }
    });
    
    // Transform the response to match the expected format
    const transformedResponse = {
      success: true,
      user: res,
      message: 'User created successfully'
    };
    
    showDbNotification({ table: 'users (+migration)', action: 'create', payload: data });
    return transformedResponse;
  };

  // Contractor Verification APIs
  const createOrUpdateContractorVerification = async (data: {
    user_id?: string;
    clerkId?: string;  // CB:BEGIN add-clerkid-support
    email?: string;
    contractor_id?: string;
    license_number?: string;
    insurance_proof_url?: string;
    w9_url?: string;
    business_name?: string;
    business_address?: string;
    additional_docs?: any;
  }): Promise<{
    success: boolean;
    verification: ContractorVerification;
    isComplete: boolean;
    verified: boolean;
  }> => {
    // CB:BEGIN fix-verification-api-url-direct-fetch
    // Use getApiUrl helper for proper URL resolution
    const { getApiUrl } = await import('../lib/getApiUrl');
    const url = getApiUrl('/api/contractor-verification');
    
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorData = {};
      try {
        errorData = JSON.parse(errorText);
      } catch {}
      throw new Error(errorData?.error || errorText || `HTTP ${response.status}`);
    }
    
    const res = await response.json();
    // CB:END fix-verification-api-url-direct-fetch
    showDbNotification({ table: 'contractor_verification (+contractors)', action: 'upsert', payload: data });
    return res;
  };

  const getContractorVerification = async (user_id: string): Promise<{
    contractor_id: string;
    verified: boolean;
    verification?: ContractorVerification;
  }> => {
    return request<{
      contractor_id: string;
      verified: boolean;
      verification?: ContractorVerification;
    }>(`/api/contractor-verification/${user_id}`);
  };

  // CB:BEGIN ensure-active-project-guards
  const ensureActiveProject = async (options: EnsureActiveProjectOptions = {}): Promise<Project | null> => {
    if (typeof window === 'undefined') return null;

    const logLabel = options.logLabel || '[ensureActiveProject]';

    const readLocalStorage = (key: string): string | null => {
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    };

    const activeProjectId = readLocalStorage('active_project_id');
    if (activeProjectId && activeProjectId !== '0' && activeProjectId !== 'demo-project') {
      console.log(`${logLabel} ✅ Active project already set`, { projectId: activeProjectId });
      return null;
    }

    const homeownerId = options.homeownerId;
    const tempHomeownerId = options.tempHomeownerId;

    if (!homeownerId && !tempHomeownerId) {
      console.warn(`${logLabel} ⏭️ Skipping project creation - missing homeownerId and tempHomeownerId`, {
        tempSessionId: options.tempSessionId || null,
      });
      return null;
    }

    const storedAddress =
      options.address ||
      readLocalStorage('pendingUserAddress') ||
      readLocalStorage('enteredAddress') ||
      readLocalStorage('last_address') ||
      '';

    const sanitizedAddress = (storedAddress || '').trim();

    if (!sanitizedAddress || sanitizedAddress === 'Address to be provided') {
      console.warn(`${logLabel} ⏭️ Skipping project creation - address unavailable`, {
        tempSessionId: options.tempSessionId || null,
      });
      return null;
    }

    const storedProjectType =
      (options.projectType as ProjectTypeOption | undefined) ||
      (readLocalStorage('pendingProjectType') as ProjectTypeOption | null) ||
      (tempHomeownerId ? 'ROOF' : null); // CB:BEGIN default-roof-for-temp – estimate flow is roof; allow project creation without explicit type

    if (!storedProjectType) {
      console.warn(`${logLabel} ⏭️ Skipping project creation - project type not selected`, {
        tempSessionId: options.tempSessionId || null,
      });
      return null;
    }
    // CB:END default-roof-for-temp

    const payload: {
      homeowner_id?: string;
      temp_homeowner_id?: string;
      project_type: ProjectTypeOption;
      address: string;
      questionnaire?: Record<string, unknown>;
    } = {
      project_type: storedProjectType,
      address: sanitizedAddress,
    };

    if (homeownerId) {
      payload.homeowner_id = homeownerId;
    } else if (tempHomeownerId) {
      payload.temp_homeowner_id = tempHomeownerId;
      // CB:BEGIN ensure-active-project-questionnaire – pass questionnaire from temp homeowner meta when available
      const tempSessionId = options.tempSessionId;
      if (tempSessionId) {
        try {
          const temp = await getTempHomeowner(tempSessionId);
          const meta = temp?.meta;
          const q = meta?.questionnaireData ?? meta?.questionnaire;
          if (q && typeof q === 'object') payload.questionnaire = q;
        } catch {
          // ignore; API will fall back to loading from temp homeowner by id
        }
      }
      // CB:END ensure-active-project-questionnaire
    }

    if (!payload.homeowner_id && !payload.temp_homeowner_id) {
      console.warn(`${logLabel} ⏭️ Skipping project creation - payload missing identifiers`);
      return null;
    }

    console.log(`${logLabel} 📝 Creating project`, {
      homeownerId: payload.homeowner_id || null,
      tempHomeownerId: payload.temp_homeowner_id || null,
      projectType: storedProjectType,
      addressPreview: `${sanitizedAddress.slice(0, 48)}${sanitizedAddress.length > 48 ? '…' : ''}`,
      tempSessionId: options.tempSessionId || null,
    });

    try {
      const project = await createProject(payload);
      try {
        localStorage.setItem('active_project_id', project.id);
      } catch {}
      console.log(`${logLabel} ✅ Project created`, { projectId: project.id });
      return project;
    } catch (error) {
      console.error(`${logLabel} ❌ Failed to create project`, error);
      return null;
    }
  };
  // CB:END ensure-active-project-guards

  return {
    // Temp Homeowner
    createOrUpdateTempHomeowner,
    getTempHomeowner,
    
    // Homeowner
    updateHomeowner,
    getHomeowner,
    
    // Projects
    createProject,
    updateProject,
    getProjects,
    fetchCurrentBidsForContractor,
    fetchActiveProjectsForContractor,
    
    // Signup
    processSignup,
    
    // Contractor Verification
    createOrUpdateContractorVerification,
    getContractorVerification,

    // Project helpers
    ensureActiveProject,
  };
}

// Utility function to generate temp session ID
export function generateTempSessionId(): string {
  return 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
}

// Utility function to get temp session ID from localStorage
export function getTempSessionId(): string {
  let tempSessionId = localStorage.getItem('temp_session_id');
  if (!tempSessionId) {
    tempSessionId = generateTempSessionId();
    localStorage.setItem('temp_session_id', tempSessionId);
  }
  return tempSessionId;
}

// Utility function to clear temp session ID
export function clearTempSessionId(): void {
  localStorage.removeItem('temp_session_id');
} 